import {
  useCanManagePaymentCharges,
  useCanRefundPaymentCharges,
} from '@/shared/hooks';
import {
  financeMethodLabel,
  formatCurrency,
  formatDateToBrazil,
} from '@/shared/utils';
import {
  Button,
  DataTable,
  Tag,
  Tooltip,
  type DataTableColumn,
} from '@noorden-insurtech/alba-design-system';
import {
  Ban,
  CalendarDays,
  Copy,
  Download,
  ExternalLink,
  History,
  Link2,
  Loader2,
  Mail,
  RefreshCw,
  Send,
  Undo2,
  Wallet,
} from 'lucide-react';
import { ConfirmActionModal } from '@/shared/ui/confirm-action-modal.component';
import { PremiumCard } from '../../proposals/components/proposal-steps/premium-card.component';
import { useCallback, useMemo, useState, type ReactNode } from 'react';
import type {
  InstallmentItem,
  PolicyDetailsPaymentInfo,
  PolicyDetailsResponse,
} from '../../../services/policies/types';

import type {
  PolicyCharge,
  PolicyChargeNotification,
  PolicyChargeRefund,
} from '../../../services/policy-charges';
import { toaster } from '../../../shared/ui/toaster.component';
import { recurrenceSummary } from '../utils/recurrence-summary.util';

import {
  useInstallmentPdf,
  useInstallments,
  useCancelPolicyRecurrence,
  usePolicyCharges,
  usePolicyChargeRefunds,
  useRefreshPolicyRecurrence,
  useRefundPolicyCharges,
  useRegeneratePolicyCharge,
  useResendPolicyChargeNotification,
} from '../hooks';

import { ChargeAttemptsDialog } from './charge-attempts.dialog';
import { ManualChargeDialog } from './manual-charge.dialog';

import type { BrokerWriteGuard } from '../../brokers/utils/broker-status.util';
import { isInactiveBrokerMessage } from '../../../shared/utils/inactive-broker-toast.util';

type BillingTabProps = {
  data: PolicyDetailsResponse;
  product: string;
  proposalNumber: string;
  status: string;
  /** Corretora inativa: "Baixar boleto" travado (a Api barra com 403). */
  downloadGuard?: BrokerWriteGuard;
};

function ensurePaymentInfoArray(
  paymentInfo?: BillingTabProps['data']['paymentInfo']
): PolicyDetailsPaymentInfo[] {
  if (!paymentInfo) return [];
  if (Array.isArray(paymentInfo)) return paymentInfo;
  return [paymentInfo];
}

/**
 * Builds a Set of installment sequence numbers (installmentPeriodSeq)
 * that belong to a payment method whose code corresponds to "boleto".
 * Uses the same logic as policy-emission.workflow (paymentMethod code → description).
 */
function buildBoletoInstallmentNumbers(
  paymentInfo?: BillingTabProps['data']['paymentInfo']
): Set<number> {
  const boletoNumbers = new Set<number>();
  const paymentInfoArray = ensurePaymentInfoArray(paymentInfo);

  for (const info of paymentInfoArray) {
    if (!isBoletoPaymentMethod(info.paymentMethod)) continue;

    if (info.installmentList) {
      for (const installment of info.installmentList) {
        if (installment.installmentPeriodSeq != null) {
          boletoNumbers.add(installment.installmentPeriodSeq);
        }
      }
    }
  }

  return boletoNumbers;
}

// Mapa de códigos de forma de pagamento (CodForma) do InsureMO → descrição
const PAYMENT_METHOD_MAP: Record<string, string> = {
  '1000013': 'Boleto Bancário',
  '1000025': 'Débito em Conta',
  '1000037': 'Cartão de Crédito',
  '1000049': 'PIX',
  '1000061': 'Transferência Bancária',
};

const BOLETO_KEYWORD = 'boleto';

function isBoletoPaymentMethod(code?: string): boolean {
  if (!code) return false;
  const description = PAYMENT_METHOD_MAP[code.trim()];
  return description?.toLowerCase().includes(BOLETO_KEYWORD) ?? false;
}

const statusConfig: Record<
  InstallmentItem['status'],
  { label: string; colorScheme: 'lightGreen' | 'error' | 'purple' | 'gray' }
> = {
  paid: { label: 'Paga', colorScheme: 'lightGreen' },
  overdue: { label: 'Vencida', colorScheme: 'error' },
  pending: { label: 'A vencer', colorScheme: 'purple' },
  cancelled: { label: 'Cancelada', colorScheme: 'gray' },
};

/**
 * Status da COBRANÇA por link (Finance.API), distinto do status da parcela no
 * I4Pro logo acima: uma parcela pode estar "A vencer" no I4Pro e o link já ter
 * sido pago, ou nem ter link ainda.
 */
/**
 * Botão de ícone das colunas de ação da aba (COBRANÇA e BAIXAR), com tooltip do
 * design system.
 *
 * Nenhum desses ícones tem rótulo, e o `title` nativo só aparece depois de um
 * segundo largo de hover — tempo suficiente para o usuário desistir e clicar
 * para descobrir. Numa linha em que um dos botões dispara uma cobrança,
 * descobrir clicando é caro.
 */
function ChargeActionButton({
  label,
  onClick,
  disabled,
  children,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  children: ReactNode;
}) {
  return (
    <Tooltip.Root
      openDelay={100}
      closeDelay={0}
      positioning={{ placement: 'top' }}
    >
      <Tooltip.Trigger asChild>
        {/*
          `aria-disabled` em vez do `disabled` nativo: botão desabilitado não
          emite evento de mouse, e o tooltip não abriria — justo quando ele
          carrega o MOTIVO do bloqueio, que é a única explicação que o usuário
          tem. O clique é barrado aqui mesmo.
        */}
        <button
          type='button'
          aria-label={label}
          aria-disabled={disabled || undefined}
          onClick={() => {
            if (!disabled) onClick();
          }}
          className={`flex items-center justify-center border-none bg-transparent text-alba-gray-500 transition-colors ${
            disabled
              ? 'cursor-not-allowed opacity-50'
              : 'cursor-pointer hover:text-alba-gray-700'
          }`}
        >
          {children}
        </button>
      </Tooltip.Trigger>
      <Tooltip.Positioner>
        <Tooltip.Content>
          <Tooltip.Arrow />
          {label}
        </Tooltip.Content>
      </Tooltip.Positioner>
    </Tooltip.Root>
  );
}

/**
 * Link de pagamento como BOTÃO, não como a URL escrita na célula.
 *
 * O endereço do provedor é longo e não diz nada a quem lê (`.../lnk_cvcfosx5`):
 * mesmo truncado ele quebrava em duas linhas e dobrava a altura da linha da
 * tabela, sem informar nada. O botão nomeia a AÇÃO; o endereço inteiro continua
 * no tooltip e no botão de copiar, que é como o corretor repassa o link ao
 * segurado.
 *
 * Continua sendo um `<a>` por baixo: um `<button>` de verdade perderia o clique
 * do meio, o "abrir em nova aba" do menu do navegador e o "copiar endereço do
 * link" — tudo que quem opera cobrança usa o tempo todo.
 */
function PaymentLinkButton({ url }: { url: string }) {
  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(url);
    toaster.create({
      type: 'success',
      title: 'Link de pagamento copiado para a área de transferência',
    });
  }, [url]);

  return (
    <span className='inline-flex items-center gap-1'>
      <Tooltip.Root
        openDelay={100}
        closeDelay={0}
        positioning={{ placement: 'top' }}
      >
        <Tooltip.Trigger asChild>
          <a
            href={url}
            target='_blank'
            rel='noopener noreferrer'
            onClick={e => e.stopPropagation()}
            className='inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-alba-purple-500 px-2.5 py-1 text-xs font-semibold text-alba-purple-500 no-underline transition-colors hover:bg-alba-purple-500 hover:text-white'
          >
            <ExternalLink className='h-3.5 w-3.5' />
            Abrir link
          </a>
        </Tooltip.Trigger>
        <Tooltip.Positioner>
          <Tooltip.Content>
            <Tooltip.Arrow />
            <span className='flex flex-col gap-0.5'>
              <span>Abrir a página de pagamento em nova aba</span>
              <span className='break-all opacity-80'>{url}</span>
            </span>
          </Tooltip.Content>
        </Tooltip.Positioner>
      </Tooltip.Root>
      <ChargeActionButton label='Copiar link de pagamento' onClick={handleCopy}>
        <Copy className='h-4 w-4' />
      </ChargeActionButton>
    </span>
  );
}

/**
 * A criação do link falhou? `EXPIRED` e `REFUSED` ficam de fora: são desfechos
 * do pagamento, não do link, e regerar não é a ação certa para eles.
 */
function chargeCreationFailed(charge: PolicyCharge): boolean {
  return charge.status === 'CREATION_FAILED' || charge.status === 'ERROR';
}

type ChargeStatusTone = 'lightGreen' | 'error' | 'purple' | 'gray' | 'yellow';

const chargeStatusConfig: Record<
  string,
  {
    label: string;
    colorScheme: ChargeStatusTone;
  }
> = {
  SCHEDULED: { label: 'Agendada', colorScheme: 'gray' },
  CREATING: { label: 'Gerando link', colorScheme: 'yellow' },
  CREATION_FAILED: { label: 'Falha ao gerar', colorScheme: 'error' },
  PENDING: { label: 'Aguardando pagamento', colorScheme: 'purple' },
  PARTIALLY_PAID: { label: 'Pagando no PIX Automático', colorScheme: 'purple' },
  PAID: { label: 'Pago', colorScheme: 'lightGreen' },
  REFUSED: { label: 'Recusado', colorScheme: 'error' },
  REFUNDED: { label: 'Estornado', colorScheme: 'gray' },
  CHARGEDBACK: { label: 'Chargeback', colorScheme: 'error' },
  EXPIRED: { label: 'Link expirado', colorScheme: 'gray' },
  ERROR: { label: 'Erro', colorScheme: 'error' },
  CANCELLED: { label: 'Cancelada', colorScheme: 'gray' },
};

/**
 * Badge da cobrança. `CANCELLED` se divide pelo motivo: "Quitada no I4Pro" diz
 * que alguém PAGOU por outro meio, e não é o mesmo recado de uma parcela
 * cancelada junto com a apólice.
 */
function resolveChargeStatusConfig(charge: PolicyCharge): {
  label: string;
  colorScheme: ChargeStatusTone;
} {
  if (
    charge.status === 'CANCELLED' &&
    charge.cancelReason === 'I4PRO_SETTLED'
  ) {
    return { label: 'Quitada no I4Pro', colorScheme: 'gray' };
  }
  return (
    chargeStatusConfig[charge.status] ?? {
      label: charge.status,
      colorScheme: 'gray',
    }
  );
}

/**
 * Parcela de cada cobrança. A cobrança única do cartão (`scope: 'POLICY'`)
 * responde por TODAS as parcelas que cobre — é o mesmo link e o mesmo status
 * em cada uma delas.
 */
function buildChargesByInstallment(
  charges: PolicyCharge[]
): Map<number, PolicyCharge> {
  const map = new Map<number, PolicyCharge>();
  const isMulti = (charge: PolicyCharge) =>
    charge.scope === 'POLICY' || charge.scope === 'RECURRENCE';

  for (const charge of charges.filter(isMulti)) {
    for (const seq of charge.coveredInstallments ?? []) map.set(seq, charge);
  }
  // O link avulso de uma parcela ("Cobrar por link" de um PIX Automático
  // recusado, cancelado ou atrasado) vale mais que o mandato que a cobria.
  for (const charge of charges.filter(item => !isMulti(item))) {
    map.set(charge.installmentNumber, charge);
  }
  return map;
}

/** Situação de UMA parcela coberta pelo mandato do PIX Automático. */
type RecurrenceRowState =
  | 'PAID'
  | 'AWAITING_AUTHORIZATION'
  | 'IN_MANDATE'
  | 'LATE'
  | 'NO_CHARGE'
  | 'CREATING';

function recurrenceRowState(
  charge: PolicyCharge,
  rowNumber: number
): RecurrenceRowState {
  const covered = [...(charge.coveredInstallments ?? [])].sort((a, b) => a - b);
  const position = covered.indexOf(rowNumber);
  if (position >= 0 && position < (charge.paidInstallments ?? 0)) return 'PAID';

  switch (charge.status) {
    case 'PENDING':
      // Autorizado no banco segue PENDING até a 1ª mensalidade paga.
      if (!charge.recurrenceAuthorizedAt) return 'AWAITING_AUTHORIZATION';
      return (charge.lateAlertedInstallment ?? 0) >= position + 1
        ? 'LATE'
        : 'IN_MANDATE';
    case 'PARTIALLY_PAID':
      return (charge.lateAlertedInstallment ?? 0) >= position + 1
        ? 'LATE'
        : 'IN_MANDATE';
    case 'CREATING':
    case 'CREATION_FAILED':
      return 'CREATING';
    default:
      return 'NO_CHARGE';
  }
}

const recurrenceRowConfig: Record<
  RecurrenceRowState,
  { label: string; colorScheme: ChargeStatusTone }
> = {
  PAID: { label: 'Paga no PIX Automático', colorScheme: 'lightGreen' },
  AWAITING_AUTHORIZATION: {
    label: 'Aguardando autorização',
    colorScheme: 'yellow',
  },
  IN_MANDATE: { label: 'No PIX Automático', colorScheme: 'purple' },
  LATE: { label: 'Atrasada no automático', colorScheme: 'error' },
  NO_CHARGE: { label: 'Sem cobrança', colorScheme: 'gray' },
  CREATING: { label: 'Gerando PIX Automático', colorScheme: 'yellow' },
};

/** O status que o banco devolveu, em palavras do corretor. */
const bankRecurrenceStatusLabel: Record<string, string> = {
  PENDING: 'aguardando o segurado autorizar',
  ACCEPTED: 'autorizado pelo segurado',
  ACTIVE: 'autorizado e gerando as cobranças',
  CANCELLED_PAYER: 'cancelado pelo segurado no banco',
  CANCELLED_RECEIVER: 'cancelado pelo recebedor',
  EXPIRED: 'prazo de autorização vencido',
  REJECTED: 'recusado pelo segurado',
};

function describeBankRecurrenceStatus(status: string | undefined): string {
  return status
    ? (bankRecurrenceStatusLabel[status] ?? status)
    : 'status não informado';
}

/** Estorno pelo Portal: só a cobrança cujo link aceitava apenas cartão. */
function isCardOnlyCharge(charge: PolicyCharge): boolean {
  return (
    charge.paymentMethods?.length === 1 &&
    charge.paymentMethods[0] === 'CREDIT_CARD'
  );
}

function isRefundInProgress(refund: PolicyChargeRefund): boolean {
  return (
    refund.status === 'REQUESTED' ||
    refund.status === 'PROCESSING' ||
    refund.status === 'PENDING'
  );
}

/** O estorno que vale para cada parcela: concluído > em andamento > o último recusado. */
function buildRefundsByInstallment(
  refunds: PolicyChargeRefund[]
): Map<number, PolicyChargeRefund> {
  const rank = (refund: PolicyChargeRefund) =>
    refund.status === 'SUCCEEDED' ? 3 : isRefundInProgress(refund) ? 2 : 1;
  const map = new Map<number, PolicyChargeRefund>();
  for (const refund of refunds) {
    for (const seq of refund.installments) {
      const current = map.get(seq);
      if (
        !current ||
        rank(refund) > rank(current) ||
        (rank(refund) === rank(current) &&
          refund.requestedAt > current.requestedAt)
      ) {
        map.set(seq, refund);
      }
    }
  }
  return map;
}

/** A parcela paga pode ser estornada agora (pagamento no cartão, sem estorno valendo)? */
function canRefundRow(
  charge: PolicyCharge,
  refund: PolicyChargeRefund | undefined
): boolean {
  return (
    charge.status === 'PAID' &&
    isCardOnlyCharge(charge) &&
    (!refund || refund.status === 'FAILED')
  );
}

/** Motivo da recusa do estorno, em palavras do financeiro. */
function describeRefundFailure(refund: PolicyChargeRefund): string {
  switch (refund.failureCode) {
    case 'CANT_REFUND_TRANSACTION_FROM_TODAY':
      return 'o cartão só aceita estorno a partir do dia seguinte ao pagamento';
    case 'REFUND_IN_PROGRESS':
      return 'já há um estorno em andamento para este pagamento';
    case 'AMOUNT_EXCEEDS_REFUNDABLE':
      return 'o valor passa do que ainda pode ser estornado';
    default:
      return (
        refund.failureMessage ??
        refund.failureCode ??
        'recusado pela adquirente'
      );
  }
}

/** O aviso depois do "Estornar", pelo que a Finance.API respondeu. */
function refundResultToast(created: PolicyChargeRefund[]) {
  const failed = created.filter(refund => refund.status === 'FAILED');
  const done = created.filter(refund => refund.status === 'SUCCEEDED');
  if (created.length > 0 && failed.length === created.length) {
    const reason = describeRefundFailure(failed[0]);
    return {
      type: 'error' as const,
      title: 'Estorno recusado',
      description: `${reason.charAt(0).toUpperCase()}${reason.slice(1)}.`,
    };
  }
  if (done.length === created.length) {
    return {
      type: 'success' as const,
      title: 'Estorno concluído',
      description:
        'O valor volta ao cartão do segurado. A equipe foi avisada para reverter a baixa no I4Pro.',
    };
  }
  return {
    type: 'info' as const,
    title: 'Estorno em andamento',
    description:
      (failed.length > 0 ? `${failed.length} recusado(s). ` : '') +
      'Aguardando a confirmação da adquirente — a aba atualiza quando ela responder.',
  };
}

/** A parcela do mandato pode ser cobrada por link avulso? */
function canChargeRowByLink(state: RecurrenceRowState): boolean {
  return state === 'NO_CHARGE' || state === 'LATE';
}

/** Cobrança de várias parcelas: a única do cartão e o mandato do PIX Automático. */
function isMultiInstallmentCharge(charge: PolicyCharge): boolean {
  return charge.scope === 'POLICY' || charge.scope === 'RECURRENCE';
}

/**
 * Nº de parcela que as AÇÕES mandam para a API. A cobrança de várias parcelas
 * vive na parcela 0, que a API não aceita como alvo de ação — ela responde pela
 * primeira parcela coberta que ainda é DELA na tela. Uma parcela do mandato que
 * ganhou link próprio ("Cobrar por link") não serve: a API acharia o link.
 */
function actionInstallmentNumber(
  charge: PolicyCharge,
  chargesByInstallment: Map<number, PolicyCharge>
): number {
  if (!isMultiInstallmentCharge(charge)) return charge.installmentNumber;
  const covered = [...(charge.coveredInstallments ?? [])].sort((a, b) => a - b);
  return (
    covered.find(seq => chargesByInstallment.get(seq) === charge) ??
    covered[0] ??
    charge.installmentNumber
  );
}

/** As ações da cobrança de várias parcelas aparecem uma vez só, na 1ª linha dela. */
function isChargeActionRow(
  charge: PolicyCharge,
  rowNumber: number,
  chargesByInstallment: Map<number, PolicyCharge>
): boolean {
  return (
    !isMultiInstallmentCharge(charge) ||
    rowNumber === actionInstallmentNumber(charge, chargesByInstallment)
  );
}

/** Envio do link ATIVO ao segurado por e-mail, se houve. */
function activeEmailNotification(
  charge: PolicyCharge
): PolicyChargeNotification | undefined {
  return charge.notifications?.find(
    item => item.channel === 'EMAIL' && item.attempt === charge.activeAttempt
  );
}

/**
 * Linhas miúdas sob o badge: quando o link agendado nasce, se é a cobrança
 * única do cartão e o que aconteceu com o e-mail ao segurado — é o que o
 * corretor precisa para responder "o cliente recebeu?".
 */
function ChargeStatusDetails({ charge }: { charge: PolicyCharge }) {
  const email = activeEmailNotification(charge);

  return (
    <>
      {charge.status === 'SCHEDULED' && charge.scheduledFor && (
        <span className='text-[11px] text-alba-gray-500'>
          Link em {formatDateToBrazil(charge.scheduledFor)}
        </span>
      )}
      {charge.scope === 'POLICY' && (
        <span className='text-[11px] text-alba-gray-500'>
          Cobrança única
          {charge.cardInstallments && charge.cardInstallments > 1
            ? ` · até ${charge.cardInstallments}x no cartão`
            : ''}
        </span>
      )}
      {charge.status === 'PENDING' && email?.status === 'SENT' && (
        <span className='text-[11px] text-alba-gray-500'>
          E-mail enviado em {formatDateToBrazil(email.sentAt)}
        </span>
      )}
      {charge.status === 'PENDING' && email?.status === 'FAILED' && (
        <span className='text-[11px] text-alba-error-red-500'>
          Falha ao enviar o e-mail
        </span>
      )}
      {charge.status === 'PENDING' && email?.status === 'SKIPPED' && (
        <span className='text-[11px] text-alba-gray-500'>
          Segurado sem e-mail
        </span>
      )}
    </>
  );
}

/**
 * Ações de uma parcela coberta pelo PIX Automático.
 *
 * Na 1ª parcela: histórico, reenviar o pedido de autorização e — só para o
 * financeiro — cancelar o mandato. Em cada parcela sem cobrança (mandato
 * recusado ou cancelado) ou atrasada: "Cobrar por link", que gera o link avulso
 * só daquela parcela.
 */
function RecurrenceRowActions({
  charge,
  rowNumber,
  actionInstallment,
  canManageCharges,
  isRegenerating,
  isResending,
  isRefreshing,
  onChargeRowByLink,
  onResendNotification,
  onRefreshRecurrence,
  onCancelRecurrence,
  onShowAttempts,
}: {
  charge: PolicyCharge;
  rowNumber: number;
  /** 1ª linha que ainda é do mandato — onde ficam as ações dele. */
  actionInstallment: number;
  canManageCharges: boolean;
  isRegenerating: boolean;
  isResending: boolean;
  isRefreshing: boolean;
  onChargeRowByLink: (installmentNumber: number, late: boolean) => void;
  onResendNotification: (installmentNumber: number) => void;
  onRefreshRecurrence: () => void;
  onCancelRecurrence: () => void;
  onShowAttempts: (installmentNumber: number) => void;
}) {
  const state = recurrenceRowState(charge, rowNumber);
  const isActionRow = rowNumber === actionInstallment;
  const active =
    charge.status === 'PENDING' || charge.status === 'PARTIALLY_PAID';
  const canCancel = canManageCharges && active && !charge.mandateCancelRequest;

  if (!isActionRow && !canChargeRowByLink(state)) {
    return <span className='text-sm text-alba-gray-400'>-</span>;
  }

  return (
    <div className='flex items-center gap-2'>
      {canChargeRowByLink(state) && (
        <ChargeActionButton
          label={
            state === 'LATE'
              ? 'Cobrar esta parcela por link (atrasada no PIX Automático)'
              : 'Cobrar esta parcela por link'
          }
          onClick={() => onChargeRowByLink(rowNumber, state === 'LATE')}
          disabled={isRegenerating}
        >
          {isRegenerating ? (
            <Loader2 className='h-4 w-4 animate-spin' />
          ) : (
            <Link2 className='h-4 w-4' />
          )}
        </ChargeActionButton>
      )}
      {isActionRow &&
        charge.status === 'PENDING' &&
        !charge.recurrenceAuthorizedAt && (
          <ChargeActionButton
            label='Reenviar ao segurado o pedido de autorização'
            onClick={() => onResendNotification(actionInstallment)}
            disabled={isResending}
          >
            {isResending ? (
              <Loader2 className='h-4 w-4 animate-spin' />
            ) : (
              <Mail className='h-4 w-4' />
            )}
          </ChargeActionButton>
        )}
      {isActionRow && active && (
        <ChargeActionButton
          label='Verificar o PIX Automático no banco'
          onClick={onRefreshRecurrence}
          disabled={isRefreshing}
        >
          <RefreshCw
            className={`h-4 w-4${isRefreshing ? ' animate-spin' : ''}`}
          />
        </ChargeActionButton>
      )}
      {isActionRow && canCancel && (
        <ChargeActionButton
          label='Cancelar o PIX Automático'
          onClick={onCancelRecurrence}
        >
          <Ban className='h-4 w-4 text-alba-error-red-500' />
        </ChargeActionButton>
      )}
      {isActionRow && (
        <ChargeActionButton
          label='Ver histórico do PIX Automático'
          onClick={() => onShowAttempts(charge.installmentNumber)}
        >
          <History className='h-4 w-4' />
        </ChargeActionButton>
      )}
    </div>
  );
}

type ColumnOptions = {
  onDownload: (row: InstallmentItem) => void;
  downloadingRow: number | null;
  boletoInstallmentNumbers: Set<number>;
  chargesByInstallment: Map<number, PolicyCharge>;
  canManageCharges: boolean;
  /** Administração ou financeiro: pode estornar parcela paga no cartão. */
  canRefund: boolean;
  refundsByInstallment: Map<number, PolicyChargeRefund>;
  onRefundInstallment: (installmentNumber: number, amount: number) => void;
  onRegenerate: (installmentNumber: number) => void;
  onShowAttempts: (installmentNumber: number) => void;
  onManualCharge: (charge: PolicyCharge) => void;
  onResendNotification: (installmentNumber: number) => void;
  onChargeRowByLink: (installmentNumber: number, late: boolean) => void;
  onRefreshRecurrence: () => void;
  isRefreshingRecurrence: boolean;
  onCancelRecurrence: () => void;
  regeneratingRow: number | null;
  resendingRow: number | null;
  downloadGuard?: BrokerWriteGuard;
};

function createBillingColumns({
  onDownload,
  downloadingRow,
  boletoInstallmentNumbers,
  chargesByInstallment,
  canManageCharges,
  canRefund,
  refundsByInstallment,
  onRefundInstallment,
  onRegenerate,
  onShowAttempts,
  onManualCharge,
  onResendNotification,
  onChargeRowByLink,
  onRefreshRecurrence,
  isRefreshingRecurrence,
  onCancelRecurrence,
  regeneratingRow,
  resendingRow,
  downloadGuard,
}: ColumnOptions): DataTableColumn<InstallmentItem>[] {
  const hasBoletoInstallments = boletoInstallmentNumbers.size > 0;
  // As colunas de link só existem quando o produto usa cobrança por link — para
  // todos os outros a tabela continua exatamente como era.
  const hasCharges = chargesByInstallment.size > 0;

  return [
    {
      accessorKey: 'number',
      header: 'Nº DA PARCELA',
      size: 120,
      cell: ({ row }) => (
        <span className='text-sm text-alba-gray-700'>
          {row.original.number}
        </span>
      ),
    },
    {
      accessorKey: 'amount',
      header: 'VALOR',
      size: 150,
      cell: ({ row }) => (
        <span className='text-sm text-alba-gray-700'>
          {formatCurrency(row.original.amount ?? 0)}
        </span>
      ),
    },
    {
      accessorKey: 'dueDate',
      header: 'DATA DE VENCIMENTO',
      size: 200,
      cell: ({ row }) => {
        const isOverdue = row.original.status === 'overdue';
        return (
          <div
            className={`flex items-center gap-1.5 ${isOverdue ? 'text-red-500' : 'text-alba-gray-700'}`}
          >
            <CalendarDays className='w-4 h-4 shrink-0' />
            <span className='text-sm'>
              {formatDateToBrazil(row.original.dueDate)}
            </span>
          </div>
        );
      },
    },
    {
      id: 'paymentDate',
      header: 'DATA DE PAGAMENTO',
      size: 200,
      cell: ({ row }) => {
        // Duas origens, porque são dois fluxos: o pagamento do LINK vem da
        // Finance.API (`charge.paidAt`, gravado pelo webhook) e o da parcela
        // tradicional vem do I4Pro. A coluna mostrava um traço fixo nos dois —
        // nunca uma data —, mesmo com a parcela marcada como Paga ao lado.
        // No PIX Automático o `paidAt` do mandato é o da ÚLTIMA mensalidade, não
        // o de cada parcela — ali vale a data da baixa no I4Pro.
        const linkedCharge = chargesByInstallment.get(row.original.number);
        const paidAt =
          (linkedCharge?.scope !== 'RECURRENCE'
            ? linkedCharge?.paidAt
            : undefined) ?? row.original.paymentDate;

        if (!paidAt) {
          return <span className='text-sm text-alba-gray-400'>-</span>;
        }

        return (
          <div className='flex items-center gap-1.5 text-alba-gray-700'>
            <CalendarDays className='w-4 h-4 shrink-0' />
            <span className='text-sm'>{formatDateToBrazil(paidAt)}</span>
          </div>
        );
      },
    },
    {
      id: 'status',
      header: 'STATUS',
      size: 120,
      cell: ({ row }) => {
        const config = statusConfig[row.original.status];
        return (
          <Tag.Root variant='solid' colorScheme={config.colorScheme} size='sm'>
            <Tag.Label>{config.label}</Tag.Label>
          </Tag.Root>
        );
      },
    },
    ...(hasCharges
      ? [
          {
            id: 'chargeStatus',
            header: 'PAGAMENTO',
            size: 180,
            cell: ({ row }: { row: { original: InstallmentItem } }) => {
              const charge = chargesByInstallment.get(row.original.number);
              if (!charge) {
                return <span className='text-sm text-alba-gray-400'>-</span>;
              }

              if (charge.scope === 'RECURRENCE') {
                const rowConfig =
                  recurrenceRowConfig[
                    recurrenceRowState(charge, row.original.number)
                  ];
                const isActionRow = isChargeActionRow(
                  charge,
                  row.original.number,
                  chargesByInstallment
                );
                const summary = isActionRow
                  ? recurrenceSummary(charge)
                  : undefined;
                return (
                  <div className='flex flex-col items-start gap-1'>
                    <Tag.Root
                      variant='solid'
                      colorScheme={rowConfig.colorScheme}
                      size='sm'
                    >
                      <Tag.Label>{rowConfig.label}</Tag.Label>
                    </Tag.Root>
                    {summary && (
                      <span className='text-[11px] text-alba-gray-500'>
                        {summary}
                      </span>
                    )}
                    {isActionRow && <ChargeStatusDetails charge={charge} />}
                  </div>
                );
              }

              // Estorno concluído ou em andamento manda na linha: o pagamento
              // dela já não é o retrato do que aconteceu com o dinheiro.
              const refund = refundsByInstallment.get(row.original.number);
              if (refund && refund.status !== 'FAILED') {
                const succeeded = refund.status === 'SUCCEEDED';
                return (
                  <div className='flex flex-col items-start gap-1'>
                    <Tag.Root
                      variant='solid'
                      colorScheme={succeeded ? 'gray' : 'yellow'}
                      size='sm'
                    >
                      <Tag.Label>
                        {succeeded ? 'Estornada' : 'Estorno em andamento'}
                      </Tag.Label>
                    </Tag.Root>
                    <span className='text-[11px] text-alba-gray-500'>
                      {succeeded
                        ? `${formatCurrency(refund.amount)} devolvidos ao cartão em ${formatDateToBrazil(refund.completedAt ?? refund.requestedAt)}`
                        : `Pedido em ${formatDateToBrazil(refund.requestedAt)}`}
                      {refund.requestedByUserName
                        ? ` por ${refund.requestedByUserName}`
                        : ''}
                    </span>
                  </div>
                );
              }

              const config = resolveChargeStatusConfig(charge);

              return (
                <div className='flex flex-col items-start gap-1'>
                  <Tag.Root
                    variant='solid'
                    colorScheme={config.colorScheme}
                    size='sm'
                  >
                    <Tag.Label>{config.label}</Tag.Label>
                  </Tag.Root>
                  <ChargeStatusDetails charge={charge} />
                  {refund?.status === 'FAILED' && (
                    <span className='text-[11px] text-alba-error-red-500'>
                      Estorno recusado: {describeRefundFailure(refund)}
                    </span>
                  )}
                  {charge.attemptCount > 1 && (
                    <button
                      type='button'
                      onClick={() => onShowAttempts(charge.installmentNumber)}
                      className='cursor-pointer border-none bg-transparent p-0 text-[11px] text-alba-gray-500 underline'
                    >
                      {charge.attemptCount} tentativas
                    </button>
                  )}
                </div>
              );
            },
          } satisfies DataTableColumn<InstallmentItem>,
          {
            id: 'chargeMethods',
            header: 'FORMA',
            size: 160,
            cell: ({ row }: { row: { original: InstallmentItem } }) => {
              const methods = chargesByInstallment.get(
                row.original.number
              )?.paymentMethods;

              if (!methods?.length) {
                return <span className='text-sm text-alba-gray-400'>-</span>;
              }

              // `charge.paymentMethods` é o que foi REALMENTE habilitado no
              // link, e não o `CodForma` da apólice: responde "por onde o
              // segurado pode pagar isto".
              return (
                <span className='text-sm text-alba-gray-700'>
                  {methods.map(financeMethodLabel).join(', ')}
                </span>
              );
            },
          } satisfies DataTableColumn<InstallmentItem>,
          {
            id: 'paymentLink',
            header: 'LINK DE PAGAMENTO',
            size: 220,
            cell: ({ row }: { row: { original: InstallmentItem } }) => {
              const charge = chargesByInstallment.get(row.original.number);

              // Mandato: o link é o de AUTORIZAÇÃO — só enquanto aguarda, e uma
              // vez só, na 1ª parcela coberta.
              if (
                !charge?.paymentUrl ||
                (charge.scope === 'RECURRENCE' &&
                  (charge.status !== 'PENDING' ||
                    Boolean(charge.recurrenceAuthorizedAt) ||
                    !isChargeActionRow(
                      charge,
                      row.original.number,
                      chargesByInstallment
                    )))
              ) {
                return <span className='text-sm text-alba-gray-400'>-</span>;
              }

              return <PaymentLinkButton url={charge.paymentUrl} />;
            },
          } satisfies DataTableColumn<InstallmentItem>,
          {
            id: 'chargeActions',
            header: 'COBRANÇA',
            size: 120,
            cell: ({ row }: { row: { original: InstallmentItem } }) => {
              const charge = chargesByInstallment.get(row.original.number);
              if (!charge) {
                return <span className='text-sm text-alba-gray-400'>-</span>;
              }

              if (charge.scope === 'RECURRENCE') {
                const actionInstallment = actionInstallmentNumber(
                  charge,
                  chargesByInstallment
                );
                return (
                  <RecurrenceRowActions
                    charge={charge}
                    rowNumber={row.original.number}
                    actionInstallment={actionInstallment}
                    canManageCharges={canManageCharges}
                    isRegenerating={regeneratingRow === row.original.number}
                    isResending={resendingRow === actionInstallment}
                    onChargeRowByLink={onChargeRowByLink}
                    onResendNotification={onResendNotification}
                    isRefreshing={isRefreshingRecurrence}
                    onRefreshRecurrence={onRefreshRecurrence}
                    onCancelRecurrence={onCancelRecurrence}
                    onShowAttempts={onShowAttempts}
                  />
                );
              }

              // Parcela paga (ou já devolvida) não recebe link novo: cobraria em
              // duplicidade, e a API recusa de qualquer forma. A única ação é o
              // estorno no cartão, parcela a parcela, para a administração e o
              // financeiro. A transação única é um pedido só e estorna o total,
              // pelo "Estornar apólice".
              if (
                charge.status === 'PAID' ||
                charge.status === 'REFUNDED' ||
                charge.status === 'CHARGEDBACK'
              ) {
                const refund = refundsByInstallment.get(row.original.number);
                if (
                  canRefund &&
                  charge.scope !== 'POLICY' &&
                  canRefundRow(charge, refund)
                ) {
                  return (
                    <ChargeActionButton
                      label='Estornar esta parcela no cartão'
                      onClick={() =>
                        onRefundInstallment(
                          row.original.number,
                          row.original.amount
                        )
                      }
                    >
                      <Undo2 className='h-4 w-4' />
                    </ChargeActionButton>
                  );
                }
                return <span className='text-sm text-alba-gray-400'>-</span>;
              }

              // A cobrança única mostra as ações só na primeira parcela.
              if (
                !isChargeActionRow(
                  charge,
                  row.original.number,
                  chargesByInstallment
                )
              ) {
                return <span className='text-sm text-alba-gray-400'>-</span>;
              }

              const target = actionInstallmentNumber(
                charge,
                chargesByInstallment
              );
              const isRegenerating = regeneratingRow === target;
              const isResending = resendingRow === target;
              const scheduled = charge.status === 'SCHEDULED';
              const cancelled = charge.status === 'CANCELLED';
              // Link que nunca nasceu se TENTA de novo; link que existe só se
              // re-sincroniza. O botão é o mesmo, o que ele faz não é, e o
              // título tem que dizer qual dos dois.
              const failed = chargeCreationFailed(charge);

              return (
                <div className='flex items-center gap-2'>
                  {scheduled ? (
                    // Antecipar é pedido explícito, parcela a parcela: o
                    // "regerar" em lote da API pula as agendadas de propósito.
                    <ChargeActionButton
                      label={
                        charge.scheduledFor
                          ? `Gerar o link agora (agendado para ${formatDateToBrazil(charge.scheduledFor)})`
                          : 'Gerar o link agora'
                      }
                      onClick={() => onRegenerate(target)}
                      disabled={isRegenerating}
                    >
                      {isRegenerating ? (
                        <Loader2 className='h-4 w-4 animate-spin' />
                      ) : (
                        <Send className='h-4 w-4' />
                      )}
                    </ChargeActionButton>
                  ) : (
                    !cancelled && (
                      <ChargeActionButton
                        label={
                          failed
                            ? 'Tentar gerar o link de pagamento novamente'
                            : 'Atualizar o status da cobrança'
                        }
                        onClick={() => onRegenerate(target)}
                        disabled={isRegenerating}
                      >
                        {isRegenerating ? (
                          <Loader2 className='h-4 w-4 animate-spin' />
                        ) : (
                          <RefreshCw
                            className={`h-4 w-4 ${failed ? 'text-alba-error-red-500' : ''}`}
                          />
                        )}
                      </ChargeActionButton>
                    )
                  )}
                  {charge.status === 'PENDING' && charge.paymentUrl && (
                    <ChargeActionButton
                      label='Reenviar o link ao segurado por e-mail'
                      onClick={() => onResendNotification(target)}
                      disabled={isResending}
                    >
                      {isResending ? (
                        <Loader2 className='h-4 w-4 animate-spin' />
                      ) : (
                        <Mail className='h-4 w-4' />
                      )}
                    </ChargeActionButton>
                  )}
                  {canManageCharges && !scheduled && !cancelled && (
                    <ChargeActionButton
                      label='Gerar cobrança manual com valor informado'
                      onClick={() => onManualCharge(charge)}
                    >
                      <Wallet className='h-4 w-4' />
                    </ChargeActionButton>
                  )}
                  <ChargeActionButton
                    label={
                      charge.attemptCount > 1
                        ? `Ver as ${charge.attemptCount} tentativas de cobrança`
                        : 'Ver histórico de tentativas'
                    }
                    onClick={() => onShowAttempts(charge.installmentNumber)}
                  >
                    <History className='h-4 w-4' />
                  </ChargeActionButton>
                </div>
              );
            },
          } satisfies DataTableColumn<InstallmentItem>,
        ]
      : []),
    ...(hasBoletoInstallments
      ? [
          {
            id: 'download',
            header: 'BAIXAR',
            size: 80,
            cell: ({ row }: { row: { original: InstallmentItem } }) => {
              const isRowBoleto = boletoInstallmentNumbers.has(
                row.original.number
              );

              if (!isRowBoleto) {
                return <span className='text-sm text-alba-gray-400'>-</span>;
              }

              const isLoading = downloadingRow === row.original.number;
              return (
                <ChargeActionButton
                  // O motivo do bloqueio VIRA o rótulo: era o que o `title`
                  // fazia antes do tooltip, e é a única explicação que o
                  // usuário tem para um botão desabilitado.
                  label={downloadGuard?.reason ?? 'Baixar boleto'}
                  onClick={() => onDownload(row.original)}
                  disabled={isLoading || downloadGuard?.blocked}
                >
                  {isLoading ? (
                    <Loader2 className='w-4 h-4 animate-spin' />
                  ) : (
                    <Download className='w-4 h-4' />
                  )}
                </ChargeActionButton>
              );
            },
          } satisfies DataTableColumn<InstallmentItem>,
        ]
      : []),
  ];
}

export function BillingTab({
  data,
  product,
  proposalNumber,
  status,
  downloadGuard,
}: BillingTabProps) {
  const [downloadingRow, setDownloadingRow] = useState<number | null>(null);
  const [regeneratingRow, setRegeneratingRow] = useState<number | null>(null);
  const [resendingRow, setResendingRow] = useState<number | null>(null);
  const [chargeByLink, setChargeByLink] = useState<{
    installmentNumber: number;
    late: boolean;
  } | null>(null);
  const [isCancelOpen, setIsCancelOpen] = useState(false);
  const [attemptsInstallment, setAttemptsInstallment] = useState<number | null>(
    null
  );
  const [manualCharge, setManualCharge] = useState<PolicyCharge | null>(null);

  const { mutateAsync: downloadPdf } = useInstallmentPdf();
  const { data: installments = [], isLoading: isLoadingInstallments } =
    useInstallments(data.policyId);

  // Cobranças por link. Vem vazio para todo produto no fluxo InsureMO/I4Pro, e é
  // esse vazio que faz as colunas de link simplesmente não existirem.
  const { data: charges = [], error: chargesError } = usePolicyCharges(
    data.policyId
  );
  const { canManage } = useCanManagePaymentCharges();
  const { mutateAsync: regenerateCharge } = useRegeneratePolicyCharge(
    data.policyId
  );
  const { mutateAsync: resendNotification } = useResendPolicyChargeNotification(
    data.policyId
  );
  const {
    mutateAsync: cancelRecurrence,
    isPending: isCancelling,
    error: cancelError,
    reset: resetCancel,
  } = useCancelPolicyRecurrence(data.policyId);

  const totalPremium = data.grossPremium ?? data.duePremium ?? 0;
  const liquidValue =
    data.rawPremium != null ? formatCurrency(data.rawPremium) : '-';
  const iofValue = data.iof ?? 0;

  const handleDownload = useCallback(
    async (row: InstallmentItem) => {
      if (downloadingRow !== null || downloadGuard?.blocked) return;

      if (!data.policyId) {
        toaster.create({
          type: 'warning',
          title: 'Boleto indisponível',
          description:
            'Não foi possível baixar o boleto porque não foi localizada a apólice',
        });
        return;
      }

      setDownloadingRow(row.number);

      try {
        await downloadPdf({
          policyId: data.policyId,
          installmentNumber: row.number,
        });
      } catch (error) {
        // Corretora inativa: o aviso central (403) já apareceu.
        if (error instanceof Error && isInactiveBrokerMessage(error.message))
          return;
        toaster.create({
          type: 'error',
          title: 'Erro ao baixar boleto',
          description: 'Boleto ainda não disponível para esta parcela',
        });
      } finally {
        setDownloadingRow(null);
      }
    },
    [data.policyId, downloadPdf, downloadingRow, downloadGuard?.blocked]
  );

  const boletoInstallmentNumbers = useMemo(
    () => buildBoletoInstallmentNumbers(data.paymentInfo),
    [data.paymentInfo]
  );

  const chargesByInstallment = useMemo(
    () => buildChargesByInstallment(charges),
    [charges]
  );

  // Estorno no cartão: só administração e financeiro (o backend exige o mesmo).
  const { canRefund } = useCanRefundPaymentCharges();
  const { data: refunds = [] } = usePolicyChargeRefunds(
    data.policyId,
    charges.length > 0
  );
  const refundsByInstallment = useMemo(
    () => buildRefundsByInstallment(refunds),
    [refunds]
  );
  const refundableRows = useMemo(
    () =>
      installments.filter(row => {
        const charge = chargesByInstallment.get(row.number);
        return (
          charge !== undefined &&
          canRefundRow(charge, refundsByInstallment.get(row.number))
        );
      }),
    [installments, chargesByInstallment, refundsByInstallment]
  );
  const [refundTarget, setRefundTarget] = useState<{
    installmentNumber?: number;
    amount: number;
    installments: number[];
  } | null>(null);
  const {
    mutateAsync: refundCharges,
    isPending: isRefunding,
    error: refundError,
    reset: resetRefund,
  } = useRefundPolicyCharges(data.policyId);

  const handleRegenerate = useCallback(
    async (installmentNumber: number) => {
      if (regeneratingRow !== null) return;
      setRegeneratingRow(installmentNumber);

      try {
        await regenerateCharge(installmentNumber);
        toaster.create({
          type: 'success',
          title: 'Link de pagamento atualizado',
          description: `Parcela ${installmentNumber}.`,
        });
      } catch (error) {
        toaster.create({
          type: 'error',
          title: 'Erro ao gerar o link',
          description:
            error instanceof Error
              ? error.message
              : 'Tente novamente em instantes.',
        });
      } finally {
        setRegeneratingRow(null);
      }
    },
    [regenerateCharge, regeneratingRow]
  );

  const { mutateAsync: refreshRecurrence, isPending: isRefreshingRecurrence } =
    useRefreshPolicyRecurrence(data.policyId);

  const handleRefreshRecurrence = useCallback(async () => {
    try {
      const result = await refreshRecurrence();
      const bankStatus = describeBankRecurrenceStatus(result.providerStatus);
      toaster.create(
        result.changed
          ? {
              type: 'success',
              title: 'PIX Automático atualizado',
              description: `O banco mostrou: ${bankStatus}. A cobrança já foi atualizada.`,
            }
          : {
              type: 'info',
              title: 'Nada mudou',
              description: `O banco confirma: ${bankStatus}.`,
            }
      );
    } catch (error) {
      toaster.create({
        type: 'error',
        title: 'Não foi possível verificar no banco',
        description:
          error instanceof Error
            ? error.message
            : 'Tente novamente em instantes.',
      });
    }
  }, [refreshRecurrence]);

  const handleResendNotification = useCallback(
    async (installmentNumber: number) => {
      if (resendingRow !== null) return;
      setResendingRow(installmentNumber);

      try {
        await resendNotification(installmentNumber);
        toaster.create({
          type: 'success',
          title: 'Link reenviado ao segurado',
          description: `Parcela ${installmentNumber}.`,
        });
      } catch (error) {
        toaster.create({
          type: 'error',
          title: 'Erro ao reenviar o link',
          description:
            error instanceof Error
              ? error.message
              : 'Tente novamente em instantes.',
        });
      } finally {
        setResendingRow(null);
      }
    },
    [resendNotification, resendingRow]
  );

  const columns = useMemo(
    () =>
      createBillingColumns({
        onDownload: handleDownload,
        downloadingRow,
        boletoInstallmentNumbers,
        chargesByInstallment,
        canManageCharges: canManage === true,
        canRefund: canRefund === true,
        refundsByInstallment,
        onRefundInstallment: (installmentNumber, amount) => {
          resetRefund();
          setRefundTarget({
            installmentNumber,
            amount,
            installments: [installmentNumber],
          });
        },
        onRegenerate: installmentNumber =>
          void handleRegenerate(installmentNumber),
        onShowAttempts: setAttemptsInstallment,
        onManualCharge: setManualCharge,
        onResendNotification: installmentNumber =>
          void handleResendNotification(installmentNumber),
        onChargeRowByLink: (installmentNumber, late) =>
          setChargeByLink({ installmentNumber, late }),
        onRefreshRecurrence: () => void handleRefreshRecurrence(),
        isRefreshingRecurrence,
        onCancelRecurrence: () => {
          resetCancel();
          setIsCancelOpen(true);
        },
        regeneratingRow,
        resendingRow,
      }),
    [
      handleDownload,
      downloadingRow,
      boletoInstallmentNumbers,
      chargesByInstallment,
      canManage,
      canRefund,
      refundsByInstallment,
      handleRegenerate,
      handleResendNotification,
      handleRefreshRecurrence,
      isRefreshingRecurrence,
      resetCancel,
      regeneratingRow,
      resendingRow,
    ]
  );

  return (
    <div className='flex flex-col gap-6 mt-6'>
      {/* Premium Card */}
      <PremiumCard
        totalValue={formatCurrency(totalPremium)}
        liquidValue={liquidValue}
        interestPercentage=''
        interestValue=''
        iofValue={formatCurrency(iofValue)}
        product={product}
        proposalNumber={proposalNumber}
        proposalLabel='Número da Proposta'
        status={status}
        showPisCofins={false}
      />

      {/*
        Falha na LEITURA das cobranças não some com as colunas em silêncio: sem
        este aviso, um 403 fica idêntico a um produto que não usa cobrança por
        link, e foi assim que uma permissão inexistente passou despercebida.
      */}
      {chargesError && (
        <div className='rounded-lg border border-alba-error-red-200 bg-alba-error-red-50 px-4 py-3'>
          <p className='text-sm text-alba-error-red-700'>
            Não foi possível carregar as cobranças desta apólice. O link de
            pagamento e a forma escolhida não aparecem abaixo.
          </p>
          <p className='mt-1 text-xs text-alba-error-red-600'>
            {chargesError.message}
          </p>
        </div>
      )}

      {/* Grid de parcelas usando DataTable do design system */}
      {isLoadingInstallments ? (
        <div className='flex items-center justify-center py-12'>
          <Loader2 className='w-6 h-6 animate-spin text-alba-gray-400' />
        </div>
      ) : (
        <>
          {canRefund && refundableRows.length > 0 && (
            <div className='flex justify-end'>
              <Button
                variant='secondary'
                size='sm'
                onClick={() => {
                  resetRefund();
                  setRefundTarget({
                    amount: refundableRows.reduce(
                      (sum, row) => sum + row.amount,
                      0
                    ),
                    installments: refundableRows.map(row => row.number),
                  });
                }}
              >
                <Undo2 className='h-4 w-4' />
                Estornar apólice
              </Button>
            </div>
          )}

          <div className='hidden sm:block'>
            <DataTable
              columns={columns}
              data={installments}
              totalItems={installments.length}
              totalPages={1}
              pageSize={10}
            />
          </div>

          <div className='flex flex-col gap-3 sm:hidden'>
            {installments.map(row => {
              const config = statusConfig[row.status];
              const isRowBoleto = boletoInstallmentNumbers.has(row.number);
              const isLoading = downloadingRow === row.number;
              const charge = chargesByInstallment.get(row.number);

              return (
                <div
                  key={row.number}
                  className='rounded-2xl border border-alba-gray-200 bg-white px-4 py-3 shadow-[0px_2px_10px_0px_rgba(15,22,30,0.06)]'
                >
                  <div className='flex items-center justify-between'>
                    <span className='text-sm font-semibold text-alba-black-500'>
                      Parcela {row.number}
                    </span>
                    <Tag.Root
                      variant='solid'
                      colorScheme={config.colorScheme}
                      size='sm'
                    >
                      <Tag.Label>{config.label}</Tag.Label>
                    </Tag.Root>
                  </div>

                  <div className='mt-3 flex flex-col gap-2'>
                    <div className='flex items-center justify-between'>
                      <span className='text-xs font-medium text-alba-gray-500'>
                        Valor
                      </span>
                      <span className='text-sm text-alba-gray-700'>
                        {formatCurrency(row.amount ?? 0)}
                      </span>
                    </div>

                    <div className='h-px w-full bg-alba-gray-200' />

                    <div className='flex items-center justify-between'>
                      <span className='text-xs font-medium text-alba-gray-500'>
                        Vencimento
                      </span>
                      <div className='flex items-center gap-1 text-sm text-alba-gray-700'>
                        <CalendarDays className='h-4 w-4' />
                        <span>{formatDateToBrazil(row.dueDate)}</span>
                      </div>
                    </div>

                    {charge && (
                      <>
                        <div className='h-px w-full bg-alba-gray-200' />
                        <div className='flex items-center justify-between'>
                          <span className='text-xs font-medium text-alba-gray-500'>
                            Pagamento
                          </span>
                          <div className='flex flex-col items-end gap-1'>
                            {charge.scope === 'RECURRENCE' ? (
                              <Tag.Root
                                variant='solid'
                                colorScheme={
                                  recurrenceRowConfig[
                                    recurrenceRowState(charge, row.number)
                                  ].colorScheme
                                }
                                size='sm'
                              >
                                <Tag.Label>
                                  {
                                    recurrenceRowConfig[
                                      recurrenceRowState(charge, row.number)
                                    ].label
                                  }
                                </Tag.Label>
                              </Tag.Root>
                            ) : (
                              <Tag.Root
                                variant='solid'
                                colorScheme={
                                  resolveChargeStatusConfig(charge).colorScheme
                                }
                                size='sm'
                              >
                                <Tag.Label>
                                  {resolveChargeStatusConfig(charge).label}
                                </Tag.Label>
                              </Tag.Root>
                            )}
                            {isChargeActionRow(
                              charge,
                              row.number,
                              chargesByInstallment
                            ) && <ChargeStatusDetails charge={charge} />}
                          </div>
                        </div>

                        {refundsByInstallment.get(row.number) && (
                          <div className='flex items-center justify-between gap-2'>
                            <span className='text-xs font-medium text-alba-gray-500'>
                              Estorno
                            </span>
                            <span className='text-xs text-alba-gray-700'>
                              {(() => {
                                const refund = refundsByInstallment.get(
                                  row.number
                                ) as PolicyChargeRefund;
                                if (refund.status === 'SUCCEEDED') {
                                  return `Estornada (${formatCurrency(refund.amount)})`;
                                }
                                return refund.status === 'FAILED'
                                  ? `Recusado: ${describeRefundFailure(refund)}`
                                  : 'Em andamento';
                              })()}
                            </span>
                          </div>
                        )}

                        {charge.paidAt && charge.scope !== 'RECURRENCE' && (
                          <div className='flex items-center justify-between gap-2'>
                            <span className='text-xs font-medium text-alba-gray-500'>
                              Pago em
                            </span>
                            <span className='text-xs text-alba-gray-700'>
                              {formatDateToBrazil(charge.paidAt)}
                            </span>
                          </div>
                        )}

                        {charge.paymentUrl && (
                          <div className='flex items-center justify-between gap-2'>
                            <span className='text-xs font-medium text-alba-gray-500'>
                              Link
                            </span>
                            <PaymentLinkButton url={charge.paymentUrl} />
                          </div>
                        )}

                        {charge.attemptCount > 1 && (
                          <button
                            type='button'
                            onClick={() =>
                              setAttemptsInstallment(charge.installmentNumber)
                            }
                            className='cursor-pointer border-none bg-transparent p-0 text-left text-[11px] text-alba-gray-500 underline'
                          >
                            {charge.attemptCount} tentativas
                          </button>
                        )}
                      </>
                    )}

                    {isRowBoleto && (
                      <>
                        <div className='h-px w-full bg-alba-gray-200' />
                        <button
                          onClick={() => void handleDownload(row)}
                          disabled={isLoading || downloadGuard?.blocked}
                          title={downloadGuard?.reason}
                          className='flex items-center justify-center gap-2 rounded-lg border border-alba-gray-200 py-2 text-sm font-medium text-alba-gray-700 disabled:opacity-60'
                        >
                          {isLoading ? (
                            <Loader2 className='h-4 w-4 animate-spin' />
                          ) : (
                            <Download className='h-4 w-4' />
                          )}
                          Baixar boleto
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {data.policyId && (
        <ChargeAttemptsDialog
          isOpen={attemptsInstallment !== null}
          onClose={() => setAttemptsInstallment(null)}
          policyId={data.policyId}
          installmentNumber={attemptsInstallment ?? undefined}
        />
      )}

      <ConfirmActionModal
        isOpen={refundTarget !== null}
        onClose={() => setRefundTarget(null)}
        onConfirm={async reason => {
          const target = refundTarget;
          if (!target) return;
          try {
            const result = await refundCharges({
              installmentNumber: target.installmentNumber,
              reason: reason.trim(),
            });
            setRefundTarget(null);
            toaster.create(refundResultToast(result.created));
          } catch {
            // O erro aparece dentro do próprio modal (errorMessage).
          }
        }}
        title={
          refundTarget?.installmentNumber != null
            ? `Estornar a parcela ${refundTarget.installmentNumber}`
            : 'Estornar a apólice'
        }
        description={`Devolve ${formatCurrency(refundTarget?.amount ?? 0)} ao cartão do segurado${
          (refundTarget?.installments.length ?? 0) > 1
            ? ` (parcelas ${refundTarget?.installments.join(', ')})`
            : ''
        }.`}
        warning='A baixa no I4Pro não é desfeita automaticamente: a equipe é avisada no chat para reverter. No cartão, o estorno só é aceito a partir do dia seguinte ao pagamento.'
        valuePrompt={{
          label: 'Justificativa (fica registrada com o seu nome)',
          placeholder: 'Ex.: apólice cancelada a pedido do segurado',
          validate: value =>
            value.trim() ? undefined : 'Informe a justificativa',
        }}
        confirmLabel='Estornar'
        tone='danger'
        isLoading={isRefunding}
        errorMessage={refundError?.message ?? null}
      />

      <ConfirmActionModal
        isOpen={chargeByLink !== null}
        onClose={() => setChargeByLink(null)}
        onConfirm={() => {
          const target = chargeByLink;
          setChargeByLink(null);
          if (target) void handleRegenerate(target.installmentNumber);
        }}
        title={`Cobrar a parcela ${chargeByLink?.installmentNumber ?? ''} por link`}
        description='Gera um link de pagamento só desta parcela e o envia ao segurado por e-mail.'
        warning={
          chargeByLink?.late
            ? 'O PIX Automático continua ativo: se o banco ainda debitar esta parcela, o segurado paga duas vezes e a devolução é manual. Confirme com o segurado antes.'
            : undefined
        }
        confirmLabel='Gerar link'
        tone='primary'
      />

      <ConfirmActionModal
        isOpen={isCancelOpen}
        onClose={() => setIsCancelOpen(false)}
        onConfirm={async reason => {
          try {
            await cancelRecurrence(reason.trim());
            setIsCancelOpen(false);
            toaster.create({
              type: 'success',
              title: 'PIX Automático cancelado',
              description:
                'O banco não debita mais as parcelas. As que faltam ficam sem cobrança até você gerar os links.',
            });
          } catch {
            // O erro aparece dentro do próprio modal (errorMessage).
          }
        }}
        title='Cancelar o PIX Automático'
        description='O banco para de debitar as parcelas desta apólice. As já pagas continuam pagas; as que faltam ficam sem cobrança até alguém gerar os links por parcela.'
        valuePrompt={{
          label: 'Justificativa (fica registrada com o seu nome)',
          placeholder: 'Ex.: segurado pediu para pagar por boleto',
          validate: value =>
            value.trim() ? undefined : 'Informe a justificativa',
        }}
        confirmLabel='Cancelar PIX Automático'
        tone='danger'
        isLoading={isCancelling}
        errorMessage={cancelError?.message ?? null}
      />

      {data.policyId && (
        <ManualChargeDialog
          isOpen={manualCharge !== null}
          onClose={() => setManualCharge(null)}
          policyId={data.policyId}
          charge={manualCharge ?? undefined}
        />
      )}
    </div>
  );
}
